import React from 'react';
import { Box, BoxProps } from '@drone-js/core';
import styled, { css } from 'styled-components';
import { useSelector } from 'react-redux';
import { ScreenSize, selectScreenSize } from 'store/slice/screen';
import { FeatureName } from 'core/constant/FeatureName';
import Feature from 'component/common/Experiment/Feature';

const ContentContainerStyled = styled(Box)<BoxProps & { ignoreMaxWidth?: boolean }>`
    ${(props) =>
        !props.ignoreMaxWidth &&
        css`
            @media (min-width: 1601px) {
                max-width: 1600px !important;
            }
        `}
`;

const enforceDefaultSpacing = (isMobileScreen: boolean, isFullContentWidth = false, isWithVerticalSpace = false): BoxProps => {
    if (isFullContentWidth) {
        return { px: '0', mx: '0' };
    }
    if (isWithVerticalSpace) {
        return isMobileScreen
            ? {
                  px: '4',
                  mx: '2',
                  mt: '4',
              }
            : {
                  pt: '12',
                  pb: '16',
                  px: '8',
                  mx: 'auto',
                  my: 'auto',
              };
    }
    return isMobileScreen
        ? {
              px: '16',
          }
        : {
              px: '8',
              mx: 'auto',
              my: 'auto',
          };
};

export const newDefaultPageContainer = (screenSize: ScreenSize, searchPagePadding = false, isNoPt = false): BoxProps => {
    if (searchPagePadding) {
        return screenSize.tabletOrDesktop ? { px: '8', pt: '12', pb: '16' } : { p: '4' };
    }
    if (screenSize.desktop) {
        return { pt: isNoPt ? '0' : '24', px: '32' };
    }
    if (screenSize.tablet) {
        return { pt: isNoPt ? '0' : '24', px: '24' };
    }
    return { pt: isNoPt ? '0' : '24', px: '16' };
};

interface ContentContainerProps extends BoxProps {
    enforceDefaultSpacing?: boolean;
    isFullContentWidth?: boolean;
    isWithVerticalSpace?: boolean;
    maxWidth?: string;
    searchPagePadding?: boolean;
    isNoPt?: boolean;
}

const ContentContainer: React.FC<React.PropsWithChildren<ContentContainerProps>> = (props) => {
    const screenSize = useSelector(selectScreenSize);
    return (
        <>
            <Feature.Off name={FeatureName.PageContainerSpacing}>
                <ContentContainerStyled
                    ignoreMaxWidth={props.isFullContentWidth}
                    {...props}
                    {...(props.enforceDefaultSpacing && enforceDefaultSpacing(screenSize.mobile, props.isFullContentWidth, props.isWithVerticalSpace))}
                >
                    {props.children}
                </ContentContainerStyled>
            </Feature.Off>
            <Feature.On name={FeatureName.PageContainerSpacing}>
                <ContentContainerStyled {...props} {...newDefaultPageContainer(screenSize, props.searchPagePadding)} maxWidth={props.maxWidth} m="auto">
                    {props.children}
                </ContentContainerStyled>
            </Feature.On>
        </>
    );
};

ContentContainer.displayName = 'ContentContainer';
export default ContentContainer;

// Remove this after integrating ACT-4352, created to avoid exp dependency for detail modularize
export const ContentContainerWithSpacing: React.FC<React.PropsWithChildren<ContentContainerProps>> = (props) => {
    const screenSize = useSelector(selectScreenSize);

    return (
        <ContentContainerStyled {...props} {...newDefaultPageContainer(screenSize, props.searchPagePadding, props.isNoPt)} maxWidth={props.maxWidth} m="auto">
            {props.children}
        </ContentContainerStyled>
    );
};

ContentContainerWithSpacing.displayName = 'ContentContainerWithSpacing';
